import './overlay_joueurs_envie_v2.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchV7QueryData } from '../../shared/leaderboardShared';
import {
  loadPlayerCountriesByEpicId,
  normalizeEpicId,
  PLAYER_COUNTRY_FALLBACK_FLAG_URL,
} from '../../shared/playerCountryApi';

const PAGE_SIZE = 10;
const MAX_ENTRIES = 50;
const PAGE_INTERVAL_MS = 20000;
const REFRESH_INTERVAL_DEFAULT_MS = 10000;
const REFRESH_INTERVAL_ALIVE_MS = 2000;
const NAME_FONT_MAX_FALLBACK = 28;
const NAME_FONT_MIN = 8;
const NAME_FONT_ROW_HEIGHT_RATIO = 0.6;
const NAME_LETTER_SPACING_EM = 0.025;
const NAME_RIGHT_SAFETY_PX = 10;
const NAME_FIT_SAFETY_FACTOR = 0.94;
const FLAG_WIDTH_EM = 1.15;
const FLAG_MARGIN_EM = 0.3;

function mapTeamsToRows(teams, aliveById = {}, maxEntries = MAX_ENTRIES, playerCountries = null) {
  const rows = teams
    .map((team) => {
      const members = team?.members ? Object.values(team.members) : [];

      const memberList = members
        .map((member) => ({
          name: member?.name || '',
          epicId: normalizeEpicId(member?.ingame_id || member?.id),
        }))
        .filter((member) => member.name)
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

      const teamName = memberList.length
        ? memberList.map((member) => member.name).join(' - ')
        : (team?.name || '');

      const rowMembers = playerCountries && memberList.length
        ? memberList.map((member) => {
          const country = playerCountries[member.epicId];
          return {
            name: member.name,
            flagUrl: country?.flagUrl || PLAYER_COUNTRY_FALLBACK_FLAG_URL,
            fallbackFlagUrl: PLAYER_COUNTRY_FALLBACK_FLAG_URL,
          };
        })
        : null;

      return {
        rank: Number(team?.place || 9999),
        place: Number(team?.place || 0),
        points: Number(team?.points || 0),
        teamName,
        members: rowMembers,
        alive: !!aliveById[team?.id],
      };
    })
    .filter((row) => row.teamName)
    .sort((a, b) => (a.rank - b.rank) || (b.points - a.points))
    .map(({ place, teamName, points, alive, members }) => ({ place, teamName, points, alive, members }));

  if (!Number.isFinite(maxEntries)) {
    return rows;
  }
  return rows.slice(0, Math.max(0, maxEntries));
}

function applySimulatedAliveStatus(rows, { enabled, onlyAlive, forcedCount }) {
  if (!enabled) {
    return rows;
  }

  if (onlyAlive) {
    return rows.map((row) => ({ ...row, alive: true }));
  }

  if (!rows.length) {
    return rows;
  }

  let aliveCount;
  if (!Number.isNaN(forcedCount)) {
    aliveCount = Math.max(0, Math.min(rows.length, forcedCount));
  } else {
    const minAlive = Math.max(1, Math.floor(rows.length * 0.3));
    const maxAlive = Math.max(minAlive, Math.floor(rows.length * 0.7));
    aliveCount = Math.floor(Math.random() * (maxAlive - minAlive + 1)) + minAlive;
  }

  const indices = Array.from({ length: rows.length }, (_, index) => index);
  for (let i = indices.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  const aliveSet = new Set(indices.slice(0, aliveCount));

  return rows.map((row, index) => ({ ...row, alive: aliveSet.has(index) }));
}

function renderTeamNameContent(row) {
  if (!row.members || !row.members.length) {
    return row.teamName;
  }

  return row.members.map((member, memberIndex) => (
    <React.Fragment key={memberIndex}>
      <img
        className='popup-flag'
        src={member.flagUrl}
        alt=''
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = member.fallbackFlagUrl;
        }}
      />
      {member.name}
      {memberIndex < row.members.length - 1 ? ' - ' : ''}
    </React.Fragment>
  ));
}

function OverlayRow({ row, index, nameFontPx, nameScaleX }) {
  return (
    <div className={`popup-row ${row.alive ? 'is-alive' : 'is-dead'}`} style={{ '--row-index': `${index}` }}>
      <div className='popup-team-rank'>{row.place}</div>
      <div className='popup-team-name'>
        <span
          className='popup-team-name-inner'
          style={nameScaleX < 1 ? { transform: `scaleX(${nameScaleX})` } : undefined}
        >
          {row.alive ? <span className='popup-alive-icon' aria-hidden='true' /> : null}
          <span className='popup-team-name-text' style={{ fontSize: `${nameFontPx}px` }}>
            {renderTeamNameContent(row)}
          </span>
        </span>
      </div>
      <div className='popup-team-points'>{row.points === '' ? '' : Math.round(Number(row.points) || 0)}</div>
    </div>
  );
}

function PopUpLeaderboard() {
  const search = useLocation().search;
  const params = new URLSearchParams(search);
  const leaderboardId = params.get('id');
  const onlyAlive = params.get('alive') === 'true' || params.get('alive') === '1';
  const showFlags = params.get('flags') === 'true' || params.get('flags') === '1';
  const simulateAlive = params.get('simulate_alive') === 'true' || params.get('simulate_alive') === '1';
  const simulateAliveCount = Number.parseInt(
    params.get('simulate_alive_count') || params.get('simulate_alive_top') || '',
    10,
  );

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [nameFits, setNameFits] = useState(() =>
    Array.from({ length: PAGE_SIZE }, () => ({ size: NAME_FONT_MAX_FALLBACK, scaleX: 1 })),
  );
  const dataLayerRef = useRef(null);


  useEffect(() => {
    let cancelled = false;

    const fetchAliveMap = async () => {
      try {
        const data = await fetchV7QueryData(leaderboardId);
        const entries = Array.isArray(data?.queries?.[0]?.entries) ? data.queries[0].entries : [];
        const aliveById = {};
        entries.forEach((entry) => {
          if (entry?.id != null) {
            aliveById[entry.id] = ((Number(entry?.flags) || 0) & 2) === 2;
          }
        });
        return aliveById;
      } catch (error) {
        return {};
      }
    };

    const fetchData = async () => {
      if (!leaderboardId) {
        setRows([]);
        return;
      }

      try {
        const alivePromise = fetchAliveMap();
        const firstResponse = await fetch(`https://api.wls.gg/v5/leaderboards/${leaderboardId}?page=0`);
        const firstData = await firstResponse.json();
        const totalPages = Math.max(1, Number(firstData?.total_pages) || 1);

        let teams = Array.isArray(firstData?.teams) ? firstData.teams.slice() : [];

        if (totalPages > 1) {
          const restPages = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              fetch(`https://api.wls.gg/v5/leaderboards/${leaderboardId}?page=${i + 1}`)
                .then((response) => response.json())
                .catch(() => null),
            ),
          );
          restPages.forEach((pageData) => {
            if (Array.isArray(pageData?.teams)) {
              teams = teams.concat(pageData.teams);
            }
          });
        }

        const aliveById = await alivePromise;
        const playerCountries = showFlags
          ? await loadPlayerCountriesByEpicId(
            teams.flatMap((team) =>
              Object.values(team?.members || {}).map((member) => member?.ingame_id || member?.id)
            )
          )
          : null;

        const mappedRows = mapTeamsToRows(
          teams,
          aliveById,
          onlyAlive ? Number.POSITIVE_INFINITY : MAX_ENTRIES,
          playerCountries,
        );
        const effectiveRows = applySimulatedAliveStatus(mappedRows, {
          enabled: simulateAlive,
          onlyAlive,
          forcedCount: simulateAliveCount,
        });

        if (!cancelled) {
          setRows(effectiveRows);
        }
      } catch (error) {
        if (!cancelled) {
          setRows([]);
        }
      }
    };

    fetchData();

    if (!leaderboardId) {
      return () => {
        cancelled = true;
      };
    }

    const refreshInterval = onlyAlive ? REFRESH_INTERVAL_ALIVE_MS : REFRESH_INTERVAL_DEFAULT_MS;
    const interval = setInterval(fetchData, refreshInterval);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [leaderboardId, simulateAlive, onlyAlive, simulateAliveCount, showFlags]);

  const sourceRows = useMemo(() => {
    if (onlyAlive) {
      return rows.filter((row) => row.alive).slice(0, PAGE_SIZE);
    }
    return rows.slice(0, MAX_ENTRIES);
  }, [rows, onlyAlive]);

  const totalPages = onlyAlive ? 1 : Math.max(1, Math.ceil(sourceRows.length / PAGE_SIZE));

  useEffect(() => {
    if (onlyAlive) {
      if (page !== 0) {
        setPage(0);
      }
      return;
    }
    if (page > totalPages - 1) {
      setPage(0);
    }
  }, [page, totalPages, onlyAlive]);

  useEffect(() => {
    if (onlyAlive || totalPages <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setPage((prev) => (prev + 1) % totalPages);
    }, PAGE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [onlyAlive, totalPages]);

  const displayedRows = useMemo(() => {
    if (onlyAlive) {
      return sourceRows.slice(0, PAGE_SIZE);
    }
    const start = page * PAGE_SIZE;
    return sourceRows.slice(start, start + PAGE_SIZE);
  }, [sourceRows, page, onlyAlive]);

  const paddedRows = useMemo(() => {
    return Array.from({ length: PAGE_SIZE }, (_, index) => {
      return displayedRows[index] || { place: '', teamName: '', points: '', alive: false };
    });
  }, [displayedRows]);

  useEffect(() => {
    const computeTextWidth = (ctx, text, fontSize, flagCount = 0) => {
      ctx.font = `900 ${fontSize}px OmnesBlack, Eurostile, sans-serif`;
      const displayText = text.toUpperCase();
      const base = ctx.measureText(displayText).width;
      const spacing = Math.max(0, displayText.length - 1) * fontSize * NAME_LETTER_SPACING_EM;
      const flagsWidth = flagCount * fontSize * (FLAG_WIDTH_EM + FLAG_MARGIN_EM + NAME_LETTER_SPACING_EM);
      return base + spacing + flagsWidth + 2;
    };

    const recalcNameFontSizes = () => {
      const layerEl = dataLayerRef.current;
      if (!layerEl) {
        return;
      }

      const rowEls = Array.from(layerEl.querySelectorAll('.popup-row'));
      if (!rowEls.length) {
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const fits = paddedRows.map((row, index) => {
        const rowEl = rowEls[index];
        const nameContainerEl = rowEl?.querySelector('.popup-team-name');
        if (!row.teamName || !nameContainerEl) {
          return { size: NAME_FONT_MAX_FALLBACK, scaleX: 1 };
        }

        const rowHeight = nameContainerEl.clientHeight || rowEl.clientHeight || 0;
        const maxSize = rowHeight > 0
          ? Math.max(NAME_FONT_MIN, Math.round(rowHeight * NAME_FONT_ROW_HEIGHT_RATIO))
          : NAME_FONT_MAX_FALLBACK;

        const iconEl = rowEl.querySelector('.popup-alive-icon');
        const innerEl = rowEl.querySelector('.popup-team-name-inner');
        const innerStyle = innerEl ? window.getComputedStyle(innerEl) : null;
        const gapWidth = innerStyle ? Number.parseFloat(innerStyle.columnGap || innerStyle.gap || '0') || 0 : 0;
        const iconWidth = iconEl ? iconEl.offsetWidth + gapWidth : 0;
        const containerStyle = window.getComputedStyle(nameContainerEl);
        const paddingWidth = (Number.parseFloat(containerStyle.paddingLeft) || 0)
          + (Number.parseFloat(containerStyle.paddingRight) || 0);
        const contentWidth = Math.max(12, nameContainerEl.clientWidth - paddingWidth);
        const availableWidth = Math.max(12, contentWidth - iconWidth - NAME_RIGHT_SAFETY_PX);

        const flagCount = row.members ? row.members.length : 0;

        let fittedSize = maxSize;
        const baseWidth = computeTextWidth(ctx, row.teamName, maxSize, flagCount);
        if (baseWidth > 0) {
          fittedSize = Math.floor((availableWidth / baseWidth) * maxSize * NAME_FIT_SAFETY_FACTOR);
        }
        fittedSize = Math.max(NAME_FONT_MIN, Math.min(maxSize, fittedSize));

        while (computeTextWidth(ctx, row.teamName, fittedSize, flagCount) > availableWidth && fittedSize > NAME_FONT_MIN) {
          fittedSize -= 1;
        }
        const finalWidth = computeTextWidth(ctx, row.teamName, fittedSize, flagCount);
        const scaleX = finalWidth > availableWidth
          ? Math.max(0.4, (contentWidth - 2) / ((finalWidth + iconWidth) * 1.06))
          : 1;

        return { size: fittedSize, scaleX };
      });

      setNameFits((prev) => {
        const same = prev.length === fits.length
          && prev.every((value, index) => value.size === fits[index].size && value.scaleX === fits[index].scaleX);
        return same ? prev : fits;
      });
    };

    const frameId = window.requestAnimationFrame(recalcNameFontSizes);
    const resizeObserver = new ResizeObserver(recalcNameFontSizes);
    if (dataLayerRef.current) {
      resizeObserver.observe(dataLayerRef.current);
    }
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        recalcNameFontSizes();
      });
    }
    window.addEventListener('resize', recalcNameFontSizes);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', recalcNameFontSizes);
    };
  }, [paddedRows]);

  return (
    <div className='overlay_joueurs_'>
      <div className='popup-overlay'>
        <div className='popup-background' />

        <div className='popup-data-layer' ref={dataLayerRef}>
          {paddedRows.map((row, index) => (
            <OverlayRow
              row={row}
              index={index}
              nameFontPx={nameFits[index]?.size || NAME_FONT_MAX_FALLBACK}
              nameScaleX={nameFits[index]?.scaleX || 1}
              key={`overlay-row-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default PopUpLeaderboard;
